import { useState } from "react";
import { useTranslation } from "react-i18next";
import CurrencyIcon from "@assets/svg/CurrencyIcon.svg?react";
import {
  Control,
  Controller,
  UseFormRegister,
  useWatch,
  UseFormTrigger,
} from "react-hook-form";
import {
  SectionHeader,
  SectionTitle,
  EditButton,
  CancelButton,
  InputContainer,
  CustomInput,
  SmallInputIcon,
  FeesInputContainer,
  FormLabel,
  ButtonContainerStyled,
} from "../styles";

import { IPropertyFees } from "../types";

interface FeesSectionProps {
  fees?: {
    cleaningFee?: number;
    managementFee?: number;
    communityFee?: number;
    resortFee?: number;
    laundryFees?: number;
  };
  register: UseFormRegister<IPropertyFees>;
  control: Control<IPropertyFees>;
  onSave: () => void;
  reset: () => void;
  trigger: UseFormTrigger<IPropertyFees>;
}

const FeesSection = ({
  fees,
  register,
  control,
  onSave,
  reset,
  trigger,
}: FeesSectionProps) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const formValues = useWatch({
    control,
    name: [
      "cleaningFee",
      "managmentFee",
      "communityFee",
      "resortFee",
      "laundryFees",
    ],
  });

  const FeesInputs = [
    {
      id: 1,
      name: "cleaningFee",
      labelText: t("PROPERTIES_MANAGEMENT.CLEANING_FEES"),
      inputIcon: <CurrencyIcon />,
      defaultValue: fees?.cleaningFee?.toString() || "",
    },
    {
      id: 2,
      name: "managmentFee",
      labelText: t("PROPERTIES_MANAGEMENT.MANAGEMENT_FEES"),
      inputIcon: <CurrencyIcon />,
      defaultValue: fees?.managementFee?.toString() || "",
    },
    {
      id: 3,
      name: "communityFee",
      labelText: t("PROPERTIES_MANAGEMENT.COMMUNITY_FEES"),
      inputIcon: <CurrencyIcon />,
      defaultValue: fees?.communityFee?.toString() || "",
    },
    {
      id: 4,
      name: "laundryFees",
      labelText: t("PROPERTIES_MANAGEMENT.LINEN_FEES"),
      inputIcon: <CurrencyIcon />,
      defaultValue: fees?.laundryFees?.toString() || "",
    },
    {
      id: 5,
      name: "resortFee",
      labelText: t("PROPERTIES_MANAGEMENT.RESORT_FEES"),
      inputIcon: <CurrencyIcon />,
      defaultValue: fees?.resortFee?.toString() || "",
    },
  ];

  return (
    <>
      {/*  Fees section  */}
      <SectionHeader>
        <SectionTitle>{t("PROPERTIES_MANAGEMENT.FEES")}</SectionTitle>
        {!isEditing && (
          <EditButton onClick={() => setIsEditing(true)}>
            {t("PROPERTIES_MANAGEMENT.EDIT")}
          </EditButton>
        )}
        {isEditing && (
          <ButtonContainerStyled>
            <EditButton
              onClick={() => {
                reset();
                setIsEditing(false);
              }}
            >
              {t("COMMON.CANCEL")}
            </EditButton>
            <CancelButton
              onClick={async () => {
                // Trigger validation for all fees fields
                const isValid = await trigger([
                  "cleaningFee",
                  "managmentFee",
                  "communityFee",
                  "resortFee",
                  "laundryFees",
                ]);

                if (isValid) {
                  onSave();
                  setIsEditing(false);
                }
              }}
            >
              {t("COMMON.SAVE")}
            </CancelButton>
          </ButtonContainerStyled>
        )}
      </SectionHeader>

      {FeesInputs.map((input) => (
        <InputContainer key={input.id}>
          <FormLabel>{input.labelText}</FormLabel>

          <FeesInputContainer style={{ position: "relative" }}>
            <Controller
              name={input.name as keyof IPropertyFees}
              control={control}
              rules={{
                required: t("PROPERTIES_MANAGEMENT.THIS_FIELD_IS_REQUIRED"),
                min: {
                  value: 0,
                  message: t("PROPERTIES_MANAGEMENT.FEES_MIN_VALUE"),
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <CustomInput
                    {...field}
                    disabled={!isEditing}
                    type="number"
                    value={field.value || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value === "" ? "" : Number(value));
                    }}
                  />
                  {error && (
                    <div
                      style={{
                        top: "10px",
                        position: "absolute",
                        transform: "translateY(30px)",
                        color: "#d32f2f",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {error.message}
                    </div>
                  )}
                </>
              )}
            />
            <SmallInputIcon>{input.inputIcon}</SmallInputIcon>
          </FeesInputContainer>
        </InputContainer>
      ))}
    </>
  );
};

export default FeesSection;
